// ts-rest contract for the final approval gate, the mandate and DocuSign
// (SEP-243 sections 36, 37, 38, 40 and 43).
//
// NO ENDPOINT HERE CARRIES A VERDICT. `createSnapshot` takes a case id and
// nothing else: the gate is a precondition of `CreateFinalApprovedSnapshot`,
// which reads the evidence itself, so no caller can assert that the AML is
// clear or that the documents are in. A refusal comes back as a 400 with the
// message the gate wrote, and that message names what is missing.
//
// THE MANDATE READS THE SNAPSHOT. `downloadMandate` refuses while the case
// holds no snapshot, because section 37 forbids a document that reads a mutable
// field.
//
// DOCUSIGN SKIPS CLEANLY. `sendForSignature` answers 200 with a `skipped`
// envelope when no DocuSign key is configured, because production runs without
// one for a period (decision D6). It never fails the case for a missing key.

import { initContract } from '@ts-rest/core';
import { z } from 'zod';

import { staffActionErrorSchema } from './agent';
import { errorBody } from '../schemas/common';
import {
    auditPackSchema,
    caseTrailSchema,
    complianceConditionTextSchema,
    finalApprovalQueueSchema,
    finalApprovalStateSchema,
    mandateSignatureEnvelopeSchema,
    mandateSignaturePurposeSchema,
} from '../final-approval';

const c = initContract();

/**
 * The binary mandate download path. The browser opens it directly, and the
 * server serves it from a plain Nest route.
 */
export function mandateDownloadPath(caseId: string): string {
    return `/api/onboarding/cases/${encodeURIComponent(caseId)}/final-approval/mandate`;
}

/**
 * The upload of a signed document of the signature pack. The route is
 * multipart, with the fields `file` and `document` (`mandate` or
 * `declaration`).
 */
export function signedDocumentUploadPath(caseId: string): string {
    return `/api/onboarding/cases/${encodeURIComponent(caseId)}/final-approval/signed-documents`;
}

/** The download of one retained signed copy. */
export function signedDocumentDownloadPath(caseId: string, sourceUploadId: string): string {
    return `${signedDocumentUploadPath(caseId)}/${encodeURIComponent(sourceUploadId)}`;
}

/**
 * The resolution of one Compliance condition (SEP-318). The route is
 * multipart: the field `resolutionNote` holds the note, and the field `files`
 * holds the optional evidence files. It answers the final approval state.
 */
export function complianceConditionResolvePath(caseId: string, conditionId: string): string {
    return `/api/onboarding/cases/${encodeURIComponent(caseId)}/compliance-conditions/${encodeURIComponent(conditionId)}/resolve`;
}

/** The download of one evidence file of a Compliance condition. */
export function complianceConditionEvidencePath(
    caseId: string,
    conditionId: string,
    sourceUploadId: string,
): string {
    return `/api/onboarding/cases/${encodeURIComponent(caseId)}/compliance-conditions/${encodeURIComponent(conditionId)}/evidence/${encodeURIComponent(sourceUploadId)}`;
}

export const finalApprovalContract = c.router(
    {
        // -- The gate and the snapshot (sections 36 and 37) ------------------
        getFinalApprovalState: {
            method: 'GET',
            path: '/onboarding/cases/:caseId/final-approval',
            pathParams: z.object({ caseId: z.string() }),
            responses: { 200: finalApprovalStateSchema, 403: errorBody, 404: errorBody },
            summary: 'The final approval checklist, the snapshot and the signature pack',
        },

        createSnapshot: {
            method: 'POST',
            path: '/onboarding/cases/:caseId/final-approval/snapshot',
            pathParams: z.object({ caseId: z.string() }),
            // NO EVIDENCE ON THE WIRE. The Action reads the case itself. The
            // optional note is the note of Compliance on the approval.
            body: z.object({ note: z.string().max(2000).optional() }),
            responses: {
                200: finalApprovalStateSchema,
                // The gate refused, and the message names what is missing.
                400: staffActionErrorSchema,
                403: staffActionErrorSchema,
                404: staffActionErrorSchema,
                // One snapshot for each case. The unique index is the backstop
                // when two approvals race the precondition.
                409: staffActionErrorSchema,
            },
            summary: 'Give the final Compliance approval of one onboarding case',
        },

        // -- The hand-overs (SEP-332, SEP-334) -------------------------------
        sendForFinalApproval: {
            method: 'POST',
            path: '/onboarding/cases/:caseId/final-approval/send',
            pathParams: z.object({ caseId: z.string() }),
            body: z.object({ note: z.string().max(2000).optional() }),
            responses: {
                200: finalApprovalStateSchema,
                400: staffActionErrorSchema,
                403: staffActionErrorSchema,
                404: staffActionErrorSchema,
            },
            summary: 'Send one onboarding case to Compliance for the final approval',
        },

        returnToIm: {
            method: 'POST',
            path: '/onboarding/cases/:caseId/final-approval/return',
            pathParams: z.object({ caseId: z.string() }),
            body: z.object({ reason: z.string().trim().min(1).max(2000) }),
            responses: {
                200: finalApprovalStateSchema,
                400: staffActionErrorSchema,
                403: staffActionErrorSchema,
                404: staffActionErrorSchema,
            },
            summary: 'Return one onboarding case to the IM, with the reason',
        },

        // -- The conditions of the Compliance approval (SEP-318) ------------
        //
        // The resolution of the IM carries files, so it is a plain multipart
        // Nest route (`complianceConditionResolvePath` above).
        addComplianceCondition: {
            method: 'POST',
            path: '/onboarding/cases/:caseId/compliance-conditions',
            pathParams: z.object({ caseId: z.string() }),
            body: z.object({ text: complianceConditionTextSchema }),
            responses: {
                200: finalApprovalStateSchema,
                400: staffActionErrorSchema,
                403: staffActionErrorSchema,
                404: staffActionErrorSchema,
            },
            summary: 'Compliance adds one condition to the approved Internal Review of a case',
        },

        verifyComplianceCondition: {
            method: 'POST',
            path: '/onboarding/cases/:caseId/compliance-conditions/:conditionId/verify',
            pathParams: z.object({ caseId: z.string(), conditionId: z.string() }),
            body: z.object({}),
            responses: {
                200: finalApprovalStateSchema,
                400: staffActionErrorSchema,
                403: staffActionErrorSchema,
                404: staffActionErrorSchema,
            },
            summary: 'Compliance verifies the resolution of one condition',
        },

        returnComplianceCondition: {
            method: 'POST',
            path: '/onboarding/cases/:caseId/compliance-conditions/:conditionId/return',
            pathParams: z.object({ caseId: z.string(), conditionId: z.string() }),
            body: z.object({ reason: z.string().trim().min(1).max(2000) }),
            responses: {
                200: finalApprovalStateSchema,
                400: staffActionErrorSchema,
                403: staffActionErrorSchema,
                404: staffActionErrorSchema,
            },
            summary:
                'Compliance returns the resolution of one condition to the IM, with the reason',
        },

        listFinalApprovals: {
            method: 'GET',
            path: '/onboarding/final-approvals',
            responses: { 200: finalApprovalQueueSchema, 403: errorBody },
            summary: 'The Final approvals queue: the cases that wait, and the cases decided',
        },

        // -- The mandate (section 37, acceptance point 55) -------------------
        //
        // The download is a BINARY route and not a ts-rest entry, because
        // ts-rest carries JSON alone. It lives on the controller as a plain
        // Nest route at
        // `GET /api/onboarding/cases/:caseId/final-approval/mandate`, which is
        // the `mandate-letter.controller.ts` precedent. `mandateDownloadPath`
        // above is the one statement of that path, so the browser and the
        // server do not each spell it. The browser fetches it through
        // `apiFetch`, because the path is relative to the API origin and the
        // request must carry the session cookie.

        // -- The signature pack (section 38) ---------------------------------
        sendForSignature: {
            method: 'POST',
            path: '/onboarding/cases/:caseId/final-approval/signature',
            pathParams: z.object({ caseId: z.string() }),
            body: z.object({
                purpose: mandateSignaturePurposeSchema,
                recipients: z
                    .array(z.object({ name: z.string().min(1), email: z.string().min(1) }))
                    .min(1),
            }),
            responses: {
                // A skipped envelope is a SUCCESS: the platform recorded that no
                // signature was requested, and it named the reason.
                200: z.object({ envelope: mandateSignatureEnvelopeSchema }),
                400: staffActionErrorSchema,
                403: staffActionErrorSchema,
                404: staffActionErrorSchema,
            },
            summary: 'Send the client declaration or the final mandate for signature',
        },

        refreshSignatureStatus: {
            method: 'POST',
            path: '/onboarding/cases/:caseId/final-approval/signature/:envelopeRowId/refresh',
            pathParams: z.object({ caseId: z.string(), envelopeRowId: z.string() }),
            body: z.object({}),
            responses: {
                200: z.object({ envelope: mandateSignatureEnvelopeSchema }),
                400: staffActionErrorSchema,
                403: staffActionErrorSchema,
                404: staffActionErrorSchema,
            },
            summary: 'Read the status DocuSign reports, and retain the completed signed document',
        },

        getCaseTrail: {
            method: 'GET',
            path: '/onboarding/cases/:caseId/trail',
            pathParams: z.object({ caseId: z.string() }),
            responses: { 200: caseTrailSchema, 403: errorBody, 404: errorBody },
            summary: 'The events of the case audit trail that are not stage moves',
        },

        // -- The audit pack (section 40, acceptance point 60) -----------------
        getAuditPack: {
            method: 'GET',
            path: '/onboarding/cases/:caseId/audit-pack',
            pathParams: z.object({ caseId: z.string() }),
            responses: { 200: auditPackSchema, 403: errorBody, 404: errorBody },
            summary: 'Everything the firm holds about one case, in the order section 40 lists it',
        },
    },
    { pathPrefix: '/api' },
);
